import { useEffect, useMemo, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Search, Copy, ChevronLeft, ChevronRight, Loader2, BookOpen, Database, X, CornerDownLeft } from 'lucide-react'

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { tmApi, termsApi } from '@/lib/api'

/**
 * 术语项接口（与 TermHighlighter 一致）
 */
interface Term {
  source: string
  target: string
  context?: string
  sourceLang?: string
  targetLang?: string
}

/**
 * Concordance 检索面板属性
 * @interface ConcordancePanelProps
 * @property open - 是否显示浮层
 * @property onClose - 关闭回调
 * @property clientId - 客户 ID（用于 TM + 术语查询）
 * @property sourceLang - 项目源语言
 * @property targetLang - 项目目标语言
 * @property enableTM - 客户是否开启 TM（控制 TM Tab 显隐）
 * @property onInsertToEditor - 把文本插入到当前正在编辑的段落译文编辑器光标位置
 */
interface ConcordancePanelProps {
  open: boolean
  onClose: () => void
  clientId: string
  sourceLang: string
  targetLang: string
  enableTM: boolean
  onInsertToEditor?: (text: string) => void
}

/** 浮层尺寸 */
const PANEL_WIDTH = 440
const PANEL_HEIGHT = 'min(75vh, 640px)'
/** TM 搜索 debounce 延迟（ms） */
const SEARCH_DEBOUNCE_MS = 300
/** TM 分页大小 */
const TM_PAGE_SIZE = 20

/**
 * 复制文本到剪贴板并 toast 反馈
 * @param text - 待复制文本
 * @param label - toast 提示中的字段名（如"原文术语"）
 */
function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text)
    .then(() => toast.success(`已复制${label}`))
    .catch(() => toast.error('复制失败，请手动选中复制'))
}

/**
 * 术语检索子面板
 * 自己调 termsApi.getByClientId 查询客户全量术语（与 TerminologyManager 数据源一致），
 * 不再依赖父组件传入的 lookup 数据（lookup 按项目语言对过滤，可能因语言对不匹配导致搜不到）。
 * 前端按项目语言对做优先排序（匹配项目语言对的排前面），但全部显示，避免遗漏。
 */
function TermsSearchTab({ clientId, sourceLang, targetLang, onInsert }: {
  clientId: string
  sourceLang: string
  targetLang: string
  onInsert?: (text: string) => void
}) {
  const [search, setSearch] = useState('')

  /** 查询客户全量术语（不限语言对） */
  const { data: termsResponse, isLoading } = useQuery({
    queryKey: ['terms', 'client', clientId],
    queryFn: () => termsApi.getByClientId(clientId),
    enabled: !!clientId,
  })

  const allTerms: Term[] = (termsResponse as any)?.data || []

  /**
   * 过滤 + 排序：
   * 1. 按 source / target / context 三字段做不区分大小写的包含匹配
   * 2. 匹配项目语言对的术语排前面，其他语言对的排后面（避免完全搜不到）
   */
  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase()
    const matched = kw
      ? allTerms.filter(t =>
          (t.source || '').toLowerCase().includes(kw) ||
          (t.target || '').toLowerCase().includes(kw) ||
          (t.context || '').toLowerCase().includes(kw)
        )
      : allTerms
    // 匹配项目语言对的排前面
    return [...matched].sort((a, b) => {
      const aMatch = (a.sourceLang === sourceLang && a.targetLang === targetLang) ? 0 : 1
      const bMatch = (b.sourceLang === sourceLang && b.targetLang === targetLang) ? 0 : 1
      return aMatch - bMatch
    })
  }, [allTerms, search, sourceLang, targetLang])

  /** 统计匹配项目语言对的术语数 */
  const matchedLangCount = allTerms.filter(t =>
    t.sourceLang === sourceLang && t.targetLang === targetLang
  ).length

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索术语（原文 / 译文 / 说明）"
            className="pl-8 h-8 text-sm"
          />
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          {isLoading ? '加载中…' : `共 ${allTerms.length} 条术语（其中 ${matchedLangCount} 条匹配 ${sourceLang}→${targetLang}）`}
          {search.trim() ? ` · 命中 ${filtered.length}` : ''}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {allTerms.length === 0 ? '该客户暂无术语' : '未匹配到术语'}
          </div>
        ) : (
          <ul className="divide-y">
            {filtered.map((t, i) => {
              const isLangMatch = t.sourceLang === sourceLang && t.targetLang === targetLang
              return (
                <li key={i} className="p-3 hover:bg-accent/40 transition-colors group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5 flex-wrap">
                        <span className="font-medium text-sm text-foreground break-all">{t.source}</span>
                        <span className="text-muted-foreground text-xs">→</span>
                        <span className="font-medium text-sm text-primary break-all">{t.target}</span>
                        {/* 语言对不匹配时显示标记，提示译者该术语可能不适用当前项目 */}
                        {!isLangMatch && (
                          <Badge variant="outline" className="text-[10px] px-1 h-4 text-amber-600 border-amber-400">
                            {t.sourceLang}→{t.targetLang}
                          </Badge>
                        )}
                      </div>
                      {t.context && (
                        <p className="text-[11px] text-muted-foreground mt-1 break-all">{t.context}</p>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      {onInsert && (
                        <button
                          type="button"
                          onClick={() => onInsert(t.target)}
                          title="插入译文到编辑器光标处"
                          className="p-1 rounded hover:bg-accent text-primary border border-primary/30"
                        >
                          <CornerDownLeft className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => copyToClipboard(t.target, '译文术语')}
                        title="复制译文"
                        className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

/**
 * TM 历史译文检索子面板
 * 调用 tmApi.list（后端已支持 search + 语言对筛选 + 分页），
 * 前端按字段二次过滤实现"原文 / 译文分别搜索"。
 */
function TMSearchTab({
  clientId,
  sourceLang,
  targetLang,
  onInsert,
}: {
  clientId: string
  sourceLang: string
  targetLang: string
  onInsert?: (text: string) => void
}) {
  const [sourceSearch, setSourceSearch] = useState('')
  const [targetSearch, setTargetSearch] = useState('')
  /** debounce 后的搜索词（实际触发请求用） */
  const [debouncedSource, setDebouncedSource] = useState('')
  const [debouncedTarget, setDebouncedTarget] = useState('')
  const [page, setPage] = useState(1)

  /** 原文搜索框 debounce */
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSource(sourceSearch.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [sourceSearch])

  /** 译文搜索框 debounce */
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedTarget(targetSearch.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [targetSearch])

  /**
   * 实际传给后端的 search 参数：
   * 后端 search 是 source LIKE OR target LIKE，无法按字段区分，
   * 故取原文/译文中任一非空值作为后端查询词，再由前端按字段精确过滤。
   * 两个都有值时取原文做后端查询，前端再过滤译文。
   */
  const backendSearch = debouncedSource || debouncedTarget || undefined
  const hasQuery = !!backendSearch

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['concordance-tm', clientId, sourceLang, targetLang, backendSearch, page],
    queryFn: () => tmApi.list(clientId, {
      search: backendSearch,
      sourceLang,
      targetLang,
      page,
      pageSize: TM_PAGE_SIZE,
    }),
    enabled: hasQuery,
    placeholderData: (prev) => prev,
  })

  /**
   * 前端按字段二次过滤：
   * - debouncedSource 非空：仅保留 sourceText 包含该词的记录
   * - debouncedTarget 非空：仅保留 targetText 包含该词的记录
   * 这样实现"原文框只搜原文、译文框只搜译文、两框都填则取交集"
   */
  const items = useMemo(() => {
    const raw = (data as any)?.data?.items || []
    return raw.filter((it: { sourceText: string; targetText: string }) => {
      if (debouncedSource && !it.sourceText.toLowerCase().includes(debouncedSource.toLowerCase())) return false
      if (debouncedTarget && !it.targetText.toLowerCase().includes(debouncedTarget.toLowerCase())) return false
      return true
    })
  }, [data, debouncedSource, debouncedTarget])

  const pagination = (data as any)?.data?.pagination
  /** 由于前端二次过滤会减少条数，分页信息以后端 total 为准（粗略），实际显示以前端过滤后为准 */
  const total = pagination?.total || 0
  const totalPages = pagination?.totalPages || 0

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={sourceSearch}
            onChange={(e) => setSourceSearch(e.target.value)}
            placeholder={`按原文搜索（${sourceLang}）`}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={targetSearch}
            onChange={(e) => setTargetSearch(e.target.value)}
            placeholder={`按译文搜索（${targetLang}）`}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          {hasQuery
            ? isFetching
              ? '检索中…'
              : `本页显示 ${items.length} 条 · 共约 ${total} 条 · 第 ${page}/${totalPages || 1} 页`
            : '输入关键词搜索历史译文（可只填一个框，或两框同填取交集）'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!hasQuery ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            输入原文或译文关键词开始检索
          </div>
        ) : isLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="p-6 text-center text-sm text-destructive">
            检索失败：{(error as Error).message}
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            未匹配到历史译文
          </div>
        ) : (
          <ul className="divide-y">
            {items.map((it: { id: string; sourceText: string; targetText: string }) => (
              <li key={it.id} className="p-3 hover:bg-accent/40 transition-colors group">
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="text-[10px] px-1 h-4 shrink-0 mt-0.5">源</Badge>
                    <span className="text-sm text-foreground break-all flex-1">{it.sourceText}</span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(it.sourceText, '原文')}
                      title="复制原文"
                      className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="text-[10px] px-1 h-4 shrink-0 mt-0.5 text-primary border-primary/40">译</Badge>
                    <span className="text-sm text-primary break-all flex-1">{it.targetText}</span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      {onInsert && (
                        <button
                          type="button"
                          onClick={() => onInsert(it.targetText)}
                          title="插入译文到编辑器光标处"
                          className="p-1 rounded hover:bg-accent text-primary border border-primary/30"
                        >
                          <CornerDownLeft className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => copyToClipboard(it.targetText, '译文')}
                        title="复制译文"
                        className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 分页栏 */}
      {hasQuery && totalPages > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            上一页
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2"
            disabled={page >= totalPages || isFetching}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            下一页
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Concordance 检索面板
 *
 * fixed 定位浮层（不挤压编辑区布局），右上角弹出，提供两种检索能力：
 * 1. 术语检索：前端过滤已加载的项目术语，支持复制 / 插入到译文编辑器
 * 2. TM 历史译文检索：调后端 tmApi.list，支持原文/译文分别搜索 + 分页 + 复制 / 插入
 *
 * Escape 键关闭，点关闭按钮关闭。
 */
export function ConcordancePanel({
  open,
  onClose,
  clientId,
  sourceLang,
  targetLang,
  enableTM,
  onInsertToEditor,
}: ConcordancePanelProps) {
  /** 默认激活的 Tab：有 TM 就默认 TM，否则术语 */
  const [activeTab, setActiveTab] = useState<'tm' | 'terms'>(enableTM ? 'tm' : 'terms')

  /** Escape 键关闭浮层 */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handleKeyDown])

  /** 客户未开 TM 时强制切到术语 Tab */
  useEffect(() => {
    if (!enableTM && activeTab === 'tm') {
      setActiveTab('terms')
    }
  }, [enableTM, activeTab])

  if (!open) return null

  return (
    <div
      className="fixed z-50 flex flex-col bg-background rounded-lg border shadow-xl"
      style={{
        top: '4rem',
        right: '1rem',
        width: `${PANEL_WIDTH}px`,
        maxWidth: 'calc(100vw - 2rem)',
        height: `calc(${PANEL_HEIGHT})`,
        maxHeight: 'calc(100vh - 6rem)',
      }}
    >
      {/* 头部：标题 + 关闭按钮 */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">检索面板</span>
          <span className="text-[11px] text-muted-foreground">术语 · TM 历史译文</span>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose} title="关闭 (Esc)">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Tab 区 */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'tm' | 'terms')} className="flex-1 flex flex-col min-h-0">
        <TabsList className="grid w-full rounded-none border-b bg-transparent h-auto p-0" style={{ gridTemplateColumns: enableTM ? '1fr 1fr' : '1fr' }}>
          <TabsTrigger
            value="terms"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2 gap-1.5"
          >
            <BookOpen className="h-3.5 w-3.5" />
            术语
          </TabsTrigger>
          {enableTM && (
            <TabsTrigger
              value="tm"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2 gap-1.5"
            >
              <Database className="h-3.5 w-3.5" />
              TM 历史译文
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="terms" className="flex-1 min-h-0 mt-0 overflow-hidden">
          <TermsSearchTab clientId={clientId} sourceLang={sourceLang} targetLang={targetLang} onInsert={onInsertToEditor} />
        </TabsContent>
        {enableTM && (
          <TabsContent value="tm" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <TMSearchTab clientId={clientId} sourceLang={sourceLang} targetLang={targetLang} onInsert={onInsertToEditor} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

export default ConcordancePanel
